import { Divider, Stack } from '@mui/material';
import { ICellRendererParams } from 'ag-grid-community';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { MoreHorizIcon } from '@/shared/assets/icons/more-horiz-icon';
import { IconButton } from '@/shared/components/ui/button/icon-button/icon-button';
import { createNameRenderer } from '@/shared/components/ui/data-table/data-table.renderer';
import { PopoverController } from '@/shared/components/ui/popover/components/popover-controller/popover-controller';
import { Tags } from '@/shared/components/ui/tags/tags';
import { TruncatedTypography } from '@/shared/components/ui/truncated-text/truncated-text.styled';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { DeleteRole } from '@/shared/features/rbac/components/delete-role/delete-role';
import { DetachRole } from '@/shared/features/rbac/components/detach-role/detach-role';
import { DuplicateRole } from '@/shared/features/rbac/components/duplicate-role/duplicate-role';
import { UpdateRole } from '@/shared/features/rbac/components/update-role/update-role';
import { UpdateUserRoleLink } from '@/shared/features/rbac/components/update-user-role-link/update-user-role-link';
import { useRbacConfig } from '@/shared/features/rbac/context/rbac-config-context';
import { useLoggedInUser } from '@/shared/hooks/use-logged-in-user/use-logged-in-user';
import { UserSourceEnum } from '@/shared/types/types.generated';

export const RoleNameRenderer = createNameRenderer();

export const RoleUsedByRenderer: React.FC<ICellRendererParams> = ({ data }) => {
  const { t } = useTranslation();

  const renderTagLabel = (count: number) => {
    return (
      <TruncatedTypography variant="p9" color="primary">
        {count} {t('rbac:users')}
      </TruncatedTypography>
    );
  };

  return (
    <Stack height="100%" justifyContent="center">
      <Tags tags={data.userTags} visibleTags={0} customLabel={renderTagLabel} />
    </Stack>
  );
};

export const RoleSsoGroupMappingRenderer: React.FC<ICellRendererParams> = ({
  data,
}) => {
  return (
    <Stack height="100%" justifyContent="center">
      <Tags tags={data.ssoGroupTags} visibleTags={1} />
    </Stack>
  );
};

export const RoleActionsRenderer: React.FC<ICellRendererParams> = ({
  data,
}) => {
  const { t } = useTranslation();
  const { useTenantSettings, permissionActions } = useRbacConfig();
  const { ssoEnabled } = useTenantSettings();
  const { can } = usePermissionCheck();
  // Hide the menu entirely when none of its actions are permitted (Update is
  // always a candidate; Delete only for non-system roles).
  const hasActions =
    can(permissionActions.updateUserRole, data.userRoleId) ||
    can(permissionActions.createUserRole) ||
    (!data.isSystemRole &&
      can(permissionActions.deleteUserRole, data.userRoleId));

  const renderPopoverOptions = (handleClose: () => void) => {
    return (
      <>
        <UpdateRole role={data.raw} onClose={handleClose} />
        <DuplicateRole role={data.raw} onClose={handleClose} />
        {!data.isSystemRole && (
          <>
            <Divider />
            <DeleteRole userRoleIds={[data.userRoleId]} component="MenuItem" />
          </>
        )}
      </>
    );
  };

  const isPopoverDisabled = () => {
    return data.isSystemRole && !ssoEnabled;
  };

  const getPopoverTriggerTooltip = () => {
    if (!data.isSystemRole || ssoEnabled) {
      return '';
    }

    return t('rbac:system_role_options_tooltip');
  };

  if (!hasActions) {
    return null;
  }

  return (
    <PopoverController
      trigger={
        <IconButton
          variant="secondary"
          disabled={isPopoverDisabled()}
          tooltip={getPopoverTriggerTooltip()}
        >
          <MoreHorizIcon />
        </IconButton>
      }
    >
      {(handleClose) => renderPopoverOptions(handleClose)}
    </PopoverController>
  );
};

export const UserRoleActionsRenderer: React.FC<ICellRendererParams> = ({
  data,
}) => {
  const { t } = useTranslation();
  const { useTenantSettings, permissionActions } = useRbacConfig();
  const { isLoggedInUser, isOwner } = useLoggedInUser();
  const { manageSsoUsersInSda, ssoEnabled } = useTenantSettings();
  const { canAny } = usePermissionCheck();
  // Hide the menu when neither the update-link nor the detach action is allowed.
  const hasActions = canAny(
    [permissionActions.linkUserRole, permissionActions.unlinkUserRole],
    data.userRoleId,
  );

  const actionsDisabled =
    isLoggedInUser(data.userId) ||
    (data.user?.source !== UserSourceEnum.Sda && !manageSsoUsersInSda) ||
    (isOwner(data.userId) && data.isSystemRole);

  const renderPopoverOptions = (handleClose: () => void) => {
    return (
      <>
        <UpdateUserRoleLink
          key="update-user-role-link"
          users={[data.user]}
          userRoles={[data.raw]}
          onClose={handleClose}
        />
        <DetachRole
          userIds={[data.userId]}
          userRoleIds={[data.userRoleId]}
          onClose={handleClose}
          onSuccess={handleClose}
        />
      </>
    );
  };

  const isPopoverDisabled = () => {
    if (actionsDisabled) {
      return true;
    }
    return data.isSystemRole && !ssoEnabled;
  };

  const getPopoverTriggerTooltip = () => {
    if (!data.isSystemRole || ssoEnabled) {
      return '';
    }
    return t('rbac:system_role_options_tooltip');
  };

  if (!hasActions) {
    return null;
  }

  return (
    <PopoverController
      trigger={
        <IconButton
          variant="secondary"
          disabled={isPopoverDisabled()}
          tooltip={getPopoverTriggerTooltip()}
        >
          <MoreHorizIcon />
        </IconButton>
      }
    >
      {(handleClose) => renderPopoverOptions(handleClose)}
    </PopoverController>
  );
};
